import { APIStatus } from "@/api/APIStatus.type";
import { deleteSpecificPlayers, getUserPlayers } from "@/api/privateApi";
import { addPlayerToClub as addPlayerToClubApi } from "@/api/privateRequests/players/addPlayerToClub";
import { Filter } from "@/components/organisms/MyClubSidebars/Filters/Filters.type";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { SearchedPlayer } from "../players/SearchedPlayer.type";
import { IRootState } from "../store";
import { fetchUserNotifications } from "../user/userSlice";
import { fetchClubStats } from "./myClubSlice";

export type SortBy = "rating" | "price" | "profit";
export type SortDirection = "asc" | "desc";

export type FiltersState = {
  [key in Filter]: boolean;
};

export type ManuallyAddPlayerProperties = {
  untradeable: boolean;
  transferList: boolean;
  activeSquad: boolean;
  sbcStorage: boolean;
};

type MyPlayersState = {
  players: SearchedPlayer[];
  totalAmountOfPlayerPages: number;
  showFilters: boolean;
  status: APIStatus;
  filters: FiltersState;
  manuallyAddPlayerProperties: ManuallyAddPlayerProperties;
  isSelecting: boolean;
  selectedPlayers: SearchedPlayer[];
};

const initialState: MyPlayersState = {
  players: [],
  totalAmountOfPlayerPages: 0,
  showFilters: false,
  status: "idle",
  filters: {
    gold: false,
    silver: false,
    bronze: false,
    special: false,
    untradeables: false,
    tradeables: false,
    transferList: false,
    sbcStorage: false,
    duplicates: false,
    manuallyAdded: false,
  },
  manuallyAddPlayerProperties: {
    untradeable: false,
    transferList: false,
    activeSquad: false,
    sbcStorage: false,
  },
  isSelecting: false,
  selectedPlayers: [],
};

export const fetchPlayers = createAsyncThunk(
  "my-club/fetch-players",
  async (
    {
      page,
      sortBy,
      sortDirection,
    }: {
      page: number;
      sortBy: SortBy;
      sortDirection: SortDirection;
    },
    { getState },
  ) => {
    const state = getState() as IRootState;
    const activeFilters = getActiveFilters(state.myPlayersReducer.filters);

    const res = await getUserPlayers({
      page,
      filters: activeFilters,
      limit: 20,
      sort: sortBy,
      ascending: sortDirection === "asc",
    });
    return { players: res.players, totalAmountOfPages: res.totalPages };
  },
);

export const addPlayerToClub = createAsyncThunk(
  "my-club/add-player-to-club",
  async (player: SearchedPlayer, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const { manuallyAddPlayerProperties } = state.myPlayersReducer;
    const res = await addPlayerToClubApi({
      ...player,
      untradeable: manuallyAddPlayerProperties.untradeable,
      transferList: manuallyAddPlayerProperties.transferList,
      activeSquad: manuallyAddPlayerProperties.activeSquad,
    });
    dispatch(fetchClubStats());
    return {
      ...player,
      eaPlayerId: res.eaPlayerId,
      manuallyAdded: true,
      untradeable: manuallyAddPlayerProperties.untradeable,
      transferList: manuallyAddPlayerProperties.transferList,
      activeSquad: manuallyAddPlayerProperties.activeSquad,
      sbcStorage: manuallyAddPlayerProperties.sbcStorage,
      position: player.preferredPosition,
    };
  },
);

export const removeSelectedPlayersFromClub = createAsyncThunk(
  "my-club/remove-player-from-club",
  async (_, { dispatch, getState }) => {
    const state = getState() as IRootState;
    const selectedPlayers = state.myPlayersReducer.selectedPlayers;
    await deleteSpecificPlayers(selectedPlayers.map((p) => p.eaPlayerId));
    dispatch(fetchUserNotifications());
    dispatch(fetchClubStats());
    return selectedPlayers;
  },
);

const myPlayersSlice = createSlice({
  name: "my-players",
  initialState,
  reducers: {
    updateShowFilters(state, action: PayloadAction<boolean>) {
      state.showFilters = action.payload;
    },
    updateFilters(state, action: PayloadAction<Filter>) {
      const filter = action.payload;
      if (filter === "tradeables" && state.filters.tradeables) {
        state.filters.untradeables = false;
      } else if (filter === "untradeables" && state.filters.untradeables) {
        state.filters.tradeables = false;
      }
      state.filters[filter] = !state.filters[filter];
    },
    updateManuallyAddPlayerProperties(
      state,
      action: PayloadAction<ManuallyAddPlayerProperties>,
    ) {
      state.manuallyAddPlayerProperties = action.payload;
    },
    updateIsSelecting(state, action: PayloadAction<boolean>) {
      state.isSelecting = action.payload;
      if (!action.payload) {
        state.selectedPlayers = [];
      }
    },
    selectPlayer(state, action: PayloadAction<SearchedPlayer>) {
      state.selectedPlayers = [...state.selectedPlayers, action.payload];
    },
    deselectPlayer(state, action: PayloadAction<SearchedPlayer>) {
      state.selectedPlayers = state.selectedPlayers.filter(
        (player) => player.eaPlayerId !== action.payload.eaPlayerId,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayers.fulfilled, (state, action) => {
        state.players = action.payload.players;
        state.totalAmountOfPlayerPages = action.payload.totalAmountOfPages;
        state.status = "fulfilled";
      })
      .addCase(fetchPlayers.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchPlayers.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(addPlayerToClub.fulfilled, (state, action) => {
        state.players.unshift(action.payload);
      })
      .addCase(removeSelectedPlayersFromClub.fulfilled, (state) => {
        state.players = state.players.filter(
          (player) =>
            !state.selectedPlayers.some(
              (selectedPlayer) =>
                selectedPlayer.eaPlayerId === player.eaPlayerId,
            ),
        );
        state.selectedPlayers = [];
        state.isSelecting = false;
      });
  },
});

export const {
  updateShowFilters,
  updateFilters,
  updateManuallyAddPlayerProperties,
  updateIsSelecting,
  selectPlayer,
  deselectPlayer,
} = myPlayersSlice.actions;

export const myPlayersSelector = (state: IRootState) => state.myPlayersReducer;

// Helper function to convert filters object to Set for API calls
export const getActiveFilters = (filters: FiltersState): Set<Filter> => {
  return new Set(
    Object.entries(filters)
      .filter(([_, isActive]) => isActive)
      .map(([filter]) => filter as Filter),
  );
};

export default myPlayersSlice.reducer;
